import * as React from 'react';
import { useFilenameEditMode } from './use-filename-edit-mode';
import { useOptimisticFilenameSave } from './use-optimistic-filename-save';

export const EDITABLE_FILENAME_FORM_ID = 'editable-filename-form';

type UseEditableFilenameFormParams = {
    filename: string;
    onSave: (newFilename: string) => Promise<any>;
};

export function useEditableFilenameForm({ filename, onSave }: UseEditableFilenameFormParams) {
    let { isEditing, enterEditMode, exitEditMode } = useFilenameEditMode({ filename });
    let { displayedFilename, saveError, dismissSaveError, saveFilename } =
        useOptimisticFilenameSave({ filename, onSave });

    let inputRef = React.useRef<HTMLInputElement>(null);

    function handleEdit() {
        enterEditMode();
        requestAnimationFrame(function () {
            if (inputRef.current) {
                inputRef.current.focus();
                inputRef.current.select();
            }
        });
    }

    function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        e.stopPropagation();

        let formData = new FormData(e.currentTarget);
        let newFilename = ((formData.get('filename') as string) || '').trim();

        if (newFilename && newFilename !== displayedFilename) {
            saveFilename(newFilename);
        }

        exitEditMode();
    }

    function handleCancel() {
        if (inputRef.current) {
            inputRef.current.value = filename;
        }

        exitEditMode();
    }

    function handleKeyDown(e: React.KeyboardEvent) {
        if (e.key === 'Escape') {
            handleCancel();
        }
    }

    return {
        isEditing,
        displayedFilename,
        saveError,
        dismissSaveError,
        inputRef,
        handleEdit,
        handleSubmit,
        handleCancel,
        handleKeyDown
    };
}
